/**
 * Read-only viewer for a visit's finalized clinical record (owner side).
 * Fetches /user/bookings/:id/record; shows "not published yet" when absent.
 */
import { useEffect } from 'react';
import { FileImage, Loader2, Stethoscope, X } from 'lucide-react';
import { PrescriptionDocGrid } from '@/components/prescription-doc-grid';
import { use_booking_record } from '@/features/booking/hooks';

function fmt_date(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function BookingRecordModal({ booking_id, on_close }: { booking_id: string; on_close: () => void }) {
  const record_q = use_booking_record(booking_id);
  const record = record_q.data ?? null;
  const items = record?.prescription?.items ?? [];
  const docs = record?.prescription_documents ?? [];

  useEffect(() => {
    const on_key = (e: KeyboardEvent) => e.key === 'Escape' && on_close();
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_close]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="booking-record-modal"
      onClick={on_close}
    >
      <div className="my-8 w-full max-w-lg rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold">
            <Stethoscope className="h-5 w-5 text-primary" /> Visit record
          </h2>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted" data-testid="booking-record-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {record_q.isLoading ? (
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !record ? (
          <p className="p-6 text-sm text-muted-foreground" data-testid="booking-record-empty">
            Your vet hasn't published a record for this visit yet.
          </p>
        ) : (
          <div className="flex flex-col gap-3 p-5 text-sm">
            <p className="text-xs text-muted-foreground">{fmt_date(record.finalized_at ?? record.created_at)}</p>
            {record.chief_complaint && <Field label="Chief complaint" value={record.chief_complaint} />}
            <Field label="Diagnosis" value={record.diagnosis} />
            {record.clinical_notes && <Field label="Notes" value={record.clinical_notes} />}
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
              {record.weight_kg && <span>Weight: {record.weight_kg} kg</span>}
              {record.follow_up_on && <span>Follow-up: {fmt_date(record.follow_up_on)}</span>}
            </div>
            {(items.length > 0 || record.prescription?.advice) && (
              <div className="rounded-md bg-muted/40 p-2">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prescription</p>
                <ul className="flex flex-col gap-1.5">
                  {items.map((it) => (
                    <li key={it.id}>
                      <span className="font-medium">{it.drug_name}</span>
                      {it.strength && <span className="text-muted-foreground"> · {it.strength}</span>}
                      <span className="text-muted-foreground"> · {it.form.toLowerCase()}</span>
                      {(it.dosage || it.frequency || it.duration_days) && (
                        <span className="block text-xs text-muted-foreground">
                          {[it.dosage, it.frequency, it.duration_days ? `${it.duration_days} days` : null].filter(Boolean).join(' · ')}
                          {it.instructions ? ` — ${it.instructions}` : ''}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {record.prescription?.advice && <p className="mt-2 text-xs text-muted-foreground">{record.prescription.advice}</p>}
              </div>
            )}
            {docs.length > 0 && (
              <div className="flex flex-col gap-1.5" data-testid="booking-record-docs">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <FileImage className="h-3.5 w-3.5" /> Prescription photos
                </p>
                <PrescriptionDocGrid docs={docs} testid_prefix="booking-record-doc" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="whitespace-pre-wrap">{value}</p>
    </div>
  );
}
